/**
 * Hook for menu scope state management
 * Provides state and actions to manage menu scope selection and organization access
 *
 * @module hooks/menuScope/useMenuScopeManagement
 */
import { useCallback } from 'react';
import { useRecoilState } from 'recoil';

import { MENU_SCOPE } from 'utils/menuMaker/constants';

import { menuScopeAtom, organizationAccessAtom } from './atoms';

/**
 * Interface for menu scope state management return value
 *
 * @interface MenuScopeManagementReturn
 */
interface MenuScopeManagementReturn {
  /**
   * Currently selected menu scope
   * Can be ORGANIZATION, STORE, or null if not set
   */
  selectedScope: MENU_SCOPE | null;

  /**
   * Whether the user has organization-level access
   * Determines if organization scope is available
   * Can be null if not yet initialized (will be set by useMenuScopeEffects)
   */
  hasOrganizationAccess: boolean | null;

  /**
   * Sets the menu scope
   * Updates the global state and persists to localStorage
   *
   * @param {MENU_SCOPE} scope - The scope to set
   * @returns {void}
   */
  setMenuScope: (scope: MENU_SCOPE) => void;

  /**
   * Sets organization access permission
   *
   * @param {boolean} hasAccess - Whether the user has access
   * @returns {void}
   */
  setOrganizationAccess: (hasAccess: boolean) => void;

  /**
   * Clears the menu scope
   * Sets the scope to null
   *
   * @returns {void}
   */
  clearMenuScope: () => void;
}

/**
 * Hook for managing menu scope state using Recoil
 * Provides state management interface for menu scope selection and organization access
 *
 * @returns {MenuScopeManagementReturn} Object with menu scope state and management actions
 *
 * @example
 * const { selectedScope, setMenuScope, hasOrganizationAccess } = useMenuScopeManagement();
 *
 * // Set scope to organization
 * setMenuScope(MENU_SCOPE.ORGANIZATION);
 *
 * // Check current scope
 * if (selectedScope === MENU_SCOPE.STORE) {
 *   // Handle store scope
 * }
 */
export const useMenuScopeManagement = (): MenuScopeManagementReturn => {
  const [selectedScope, setSelectedScope] = useRecoilState(menuScopeAtom);
  const [hasOrganizationAccess, setHasOrganizationAccess] = useRecoilState(organizationAccessAtom);

  /**
   * Sets the menu scope
   *
   * @param {MENU_SCOPE} scope - The scope to set
   * @returns {void}
   */
  const setMenuScope = useCallback(
    (scope: MENU_SCOPE): void => {
      setSelectedScope(scope);
    },
    [setSelectedScope],
  );

  /**
   * Clears the menu scope
   *
   * @returns {void}
   */
  const clearMenuScope = useCallback((): void => {
    setSelectedScope(null);
  }, [setSelectedScope]);

  /**
   * Sets whether the user has organization access
   *
   * @param {boolean} hasAccess - Whether the user has access
   * @returns {void}
   */
  const setOrganizationAccess = useCallback(
    (hasAccess: boolean): void => {
      setHasOrganizationAccess(hasAccess);
    },
    [setHasOrganizationAccess],
  );

  return {
    selectedScope,
    hasOrganizationAccess,
    setMenuScope,
    setOrganizationAccess,
    clearMenuScope,
  };
};
